/**
 * LoginPromptModal Component
 * 
 * Modal shown when an anonymous user tries to do an action that requires an account.
 * Encourages sign-up while allowing them to continue exploring.
 * 
 * Actions that trigger this:
 * - Saving changes
 * - Creating a project
 * - Exporting content
 * - Inviting collaborators
 * - Any paid feature
 */

import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Icon } from './Icon'
import { Button } from './Button'
import './LoginPromptModal.css'

// Animation variants
const overlayVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
}

const modalVariants = {
  initial: {
    opacity: 0,
    scale: 0.9,
    y: 20,
  },
  animate: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 10,
    transition: {
      duration: 0.2,
    }
  },
}

// Copy for different action types
const ACTION_COPY = {
  save: {
    icon: 'save',
    title: 'Save Your Work',
    description: 'Create a free account to save your characters, worlds, and stories. Your work will be here when you come back.',
  },
  create: {
    icon: 'plus',
    title: 'Create Your Project',
    description: 'Sign up for free to create your own story project. Build characters, timelines, and worlds that persist.',
  },
  export: {
    icon: 'export',
    title: 'Export Your Content',
    description: 'Create a free account to export your story content. Download your characters, timelines, and more.',
  },
  invite: {
    icon: 'members',
    title: 'Invite Collaborators',
    description: 'Sign up to invite others to your project. Collaborate on your story universe with teammates.',
  },
  default: {
    icon: 'login',
    title: 'Sign in to continue',
    description: 'Create a free account to unlock this feature. It only takes a moment.',
  },
}

/**
 * LoginPromptModal
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when closed/dismissed
 * @param {string} props.action - The action that triggered the prompt ('save' | 'create' | 'export' | 'invite' | 'default')
 * @param {string} props.returnTo - Optional path to return to after login
 */
export function LoginPromptModal({ isOpen, onClose, action = 'default', returnTo }) {
  const navigate = useNavigate()
  const copy = ACTION_COPY[action] || ACTION_COPY.default

  const handleSignUp = () => {
    const params = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ''
    navigate(`/login${params}`)
    onClose?.()
  }

  const handleContinue = () => {
    onClose?.()
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="login-prompt-modal__overlay"
          variants={overlayVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          onClick={handleContinue}
        >
          <motion.div
            className="login-prompt-modal"
            variants={modalVariants}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-prompt-title"
          >
            {/* Decorative glow */}
            <div className="login-prompt-modal__glow" />

            {/* Header */}
            <div className="login-prompt-modal__header">
              <div className="login-prompt-modal__icon">
                <Icon name={copy.icon} size={28} />
              </div>
              <h2 id="login-prompt-title" className="login-prompt-modal__title">
                {copy.title}
              </h2>
            </div>

            {/* Body copy */}
            <p className="login-prompt-modal__body">
              {copy.description}
            </p>

            {/* Benefits list */}
            <ul className="login-prompt-modal__benefits">
              <li>
                <Icon name="check" size={16} />
                <span>Free to sign up</span>
              </li>
              <li>
                <Icon name="check" size={16} />
                <span>No credit card required</span>
              </li>
              <li>
                <Icon name="check" size={16} />
                <span>Start creating immediately</span>
              </li>
            </ul>

            {/* Actions */}
            <div className="login-prompt-modal__actions">
              <Button
                variant="primary"
                size="lg"
                fullWidth
                onClick={handleSignUp}
              >
                Sign up for free
              </Button>
              <button
                className="login-prompt-modal__btn--ghost"
                onClick={handleContinue}
              >
                Continue exploring
              </button>
            </div>

            {/* Close button */}
            <button
              className="login-prompt-modal__close"
              onClick={handleContinue}
              aria-label="Close"
            >
              <Icon name="close" size={18} />
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default LoginPromptModal
